import { del, get, patch, post, qs } from './http.ts';

export interface AdminUser {
  id: string; email: string; display_name: string; role: string; status: string;
  quota_bytes: number; used_bytes: number; created_at: string;
  device_count: number; file_count: number;
}

export interface AdminStats {
  users: number; files: number; folders: number; versions: number; blocks: number;
  logical_bytes: number; physical_bytes: number; dedup_ratio: number;
  trash_bytes: number; ws_connections: number;
}

export interface JobStatus {
  name: string; schedule: string; running: boolean;
  last_run_at: string | null; ok: boolean | null; detail: unknown;
}

export const listUsers = (search?: string): Promise<{ items: AdminUser[] }> =>
  get(`/admin/users${qs({ search, limit: 200 })}`);

export const createUser = (body: {
  email: string; password: string; display_name: string;
  role: 'user' | 'admin'; quota_bytes?: number;
}): Promise<{ id: string }> => post('/admin/users', body);

export const updateUser = (
  id: string,
  body: { quota_bytes?: number; status?: 'active' | 'suspended'; role?: 'user' | 'admin' },
): Promise<{ ok: boolean }> => patch(`/admin/users/${id}`, body);

export const stats = (): Promise<AdminStats> => get('/admin/stats');

export const jobs = (): Promise<{ jobs: JobStatus[] }> => get('/admin/jobs');

export const runJob = (name: string, graceHours?: number): Promise<{ name: string; result: unknown }> =>
  post(`/admin/jobs/${name}/run${qs({ grace_hours: graceHours })}`);

export const auditLog = (action?: string): Promise<{ items: Array<Record<string, unknown>> }> =>
  get(`/admin/audit${qs({ action, limit: 200 })}`);

export interface SettingItem {
  key: string;
  type: 'int' | 'bool';
  min?: number;
  max?: number;
  value: number | boolean;
  label: string;
  description: string;
}

export interface UserDetail extends AdminUser {
  namespaces: Array<{ id: string; name: string; type: string; role: string; files: number }>;
  devices: Array<{ id: string; name: string; platform: string; last_seen_at: string | null }>;
  sessions: number;
  trash_bytes: number;
}

export const settings = (): Promise<{ items: SettingItem[] }> => get('/admin/settings');

export const updateSettings = (
  patchBody: Record<string, number | boolean>,
): Promise<{ applied: string[] }> => patch('/admin/settings', patchBody);

export const userDetail = (id: string): Promise<UserDetail> => get(`/admin/users/${id}`);

export interface AdminRelease {
  version: string;
  channel: string;
  notes: string | null;
  released_at: string;
  is_current: boolean;
  artifacts: Array<{ os: string; arch: string; kind: string; file: string; size: number; label: string; url: string }>;
}

export const releases = (): Promise<{
  items: AdminRelease[];
  downloads: Array<{ version: string; file: string; count: number; last_at: string }>;
}> => get('/admin/releases');

export const setCurrentRelease = (version: string): Promise<{ ok: boolean }> =>
  post(`/admin/releases/${version}/current`);

export const deleteRelease = (version: string): Promise<{ ok: boolean }> =>
  del(`/admin/releases/${version}`);

/**
 * 사용자 계정 삭제. 되돌릴 수 없다.
 *
 * 이 서비스에는 메일이 없어 사용자가 스스로 탈퇴할 수 없으므로 관리자가
 * 대신 처리한다. 자기 계정과 마지막 관리자는 서버가 거부한다.
 */
export const deleteUser = (id: string): Promise<{ email: string; freed_paths: number }> =>
  del(`/admin/users/${id}`);

/**
 * 비밀번호 초기화.
 *
 * 메일 발송이 없어 "비밀번호 찾기" 가 존재하지 않는다. 사용자가 비밀번호를
 * 잊었을 때 계정을 되살릴 유일한 경로다.
 */
export const resetUserPassword = (
  id: string, password: string,
): Promise<{ email: string; revoked_sessions: number }> =>
  post(`/admin/users/${id}/password`, { password });
